/**
 * Slash Commands — Ask Argus composer shortcuts.
 *
 * PR-AAU-D3 (audit + wire). Before this PR, clicking a slash command in
 * the composer popup set the input to a literal `/{command} ` prefix and
 * sent that prefix to `/api/v1/ecosystem/query` unchanged — the LLM had
 * to interpret the slash itself, which was unreliable and non-deterministic.
 *
 * After this PR, each slash command expands to a real natural-language
 * prompt template that the user finishes filling in before sending. The
 * resulting message is a normal chat turn — no new dispatch route, no
 * special backend handling. Slash is purely a UX shortcut.
 *
 * NOT in scope for this PR:
 *   - Tenant-authored prompt templates (those live under `/templates` and
 *     are managed via /studio/prompts; integration with the composer menu
 *     is a separate Quick Actions initiative).
 *   - Backend slash parsing or new routes.
 *   - Adding NEW slash commands beyond the 8 already in the composer.
 */

export interface SlashCommandDef {
  /** Slug without the leading slash, e.g. `summarize`. */
  command: string;
  /** Short menu label. */
  label: string;
  /** Emoji icon for the menu. */
  icon: string;
  /**
   * Natural-language template inserted into the composer when the
   * command is clicked. The trailing space (or ellipsis) is intentional
   * — it gives the user an obvious caret position to continue typing.
   */
  template: string;
}

export const SLASH_COMMANDS: readonly SlashCommandDef[] = [
  {
    command: 'summarize',
    label: 'Summarize a document',
    icon: '📄',
    template:
      'Please summarize the following document for me — key findings, action items, and anything safety-critical. Document: ',
  },
  {
    command: 'compare',
    label: 'Compare two documents',
    icon: '🔀',
    template:
      'Compare these two documents and highlight the differences (procedural, numerical, safety-relevant). Document A: \n\nDocument B: ',
  },
  {
    command: 'checklist',
    label: 'Generate a checklist',
    icon: '✅',
    template:
      'Generate a step-by-step checklist for the following task. Include safety prerequisites, tools required, and verification steps. Task: ',
  },
  {
    command: 'report',
    label: 'Generate a report',
    icon: '📊',
    template:
      'Generate a structured report covering the following topic. Include an executive summary, key metrics, findings, and recommended actions. Topic: ',
  },
  {
    command: 'safety',
    label: 'Safety procedure lookup',
    icon: '⚠️',
    template:
      'Look up the safety procedure for the following situation. Cite the source SOP / manual / regulation and call out any LOTO, PPE, or hazard signal words. Situation: ',
  },
  {
    command: 'status',
    label: 'Equipment status check',
    icon: '🔧',
    template:
      'Show me the current operational status of the following equipment — recent telemetry, open work orders, alarms, and pending maintenance. Equipment: ',
  },
  {
    command: 'search',
    label: 'Search knowledge base',
    icon: '🔍',
    template:
      'Search the knowledge base for information about the following. Return the most relevant passages with citations. Query: ',
  },
  {
    command: 'calculate',
    label: 'Engineering calculation',
    icon: '🧮',
    template:
      'Help me with the following engineering calculation. Show the formula, plug in the values, and report the result with units and tolerance. Problem: ',
  },
] as const;

/** Map of command-slug → definition, for O(1) lookup. */
export const SLASH_COMMAND_MAP: Record<string, SlashCommandDef> =
  Object.freeze(
    Object.fromEntries(SLASH_COMMANDS.map((c) => [c.command, c])),
  );

/**
 * Resolve a slash command click into the text that should populate the
 * composer.
 *
 * - If `command` is a known slash, returns the expanded natural-language
 *   template (the user finishes the sentence and hits send).
 * - If `command` is NOT a known slash, falls back to the legacy
 *   `/{command} ` literal so we don't silently swallow user input.
 *
 * Pure function — does not touch the DOM. Used by chat-input.tsx and
 * directly unit-tested.
 */
export function expandSlashCommand(command: string): string {
  const def = SLASH_COMMAND_MAP[command];
  if (def) return def.template;
  return `/${command} `;
}

/**
 * fix-wave-4 #848 + #854 — what to INSERT into the composer when the
 * user picks a slash command from the menu. Returns the literal
 * `/<command> ` token (with trailing space) rather than the full
 * natural-language expansion, so the user can SEE which command they
 * picked and can backspace it without scrubbing 30 words.
 *
 * Expansion now happens at SEND time via `expandSlashCommandForSubmit`.
 */
export function insertSlashCommandToken(command: string): string {
  return `/${command} `;
}

/**
 * Detect a leading slash-command token and expand it to the full
 * natural-language template ONLY when the input is the bare literal
 * (e.g. `/summarize` or `/summarize Some doc`). Returns the original
 * `raw` unchanged when:
 *
 *   - It doesn't start with `/<knownSlash>` followed by space/EOL.
 *   - The user has already typed past the literal into the body (we
 *     leave their phrasing alone).
 *
 * On match, the template's trailing prompt cue (e.g. "Document: ") is
 * preserved and the user's free-form continuation is appended after it.
 *
 * Pure function — used by `chat-input.tsx` on submit.
 */
export function expandSlashCommandForSubmit(raw: string): string {
  // Only act on inputs that START with a slash + known command.
  // The user can still send arbitrary "/" content (the legacy
  // fallback in `expandSlashCommand` preserves unknown slashes as
  // literals) — but only RECOGNIZED commands get the natural-
  // language expansion.
  if (!raw.startsWith('/')) return raw;
  const m = raw.match(/^\/([a-zA-Z_-]+)(?:\s([\s\S]*))?$/);
  if (!m) return raw;
  const slug = m[1]!.toLowerCase();
  const continuation = m[2] ?? '';
  const def = SLASH_COMMAND_MAP[slug];
  if (!def) return raw;
  // The template usually ends with a "field: " cue (e.g. "Document: ").
  // Append the user's continuation after that cue so the LLM sees:
  //   "Please summarize the following document... Document: <user text>".
  return def.template + continuation;
}

/**
 * Filter the slash-command list for the popup. Matches against both the
 * command slug and the label, case-insensitive.
 */
export function filterSlashCommands(
  filter: string,
): readonly SlashCommandDef[] {
  const needle = filter.toLowerCase();
  if (!needle) return SLASH_COMMANDS;
  return SLASH_COMMANDS.filter(
    (c) =>
      c.command.includes(needle) || c.label.toLowerCase().includes(needle),
  );
}
